import React, { memo, useLayoutEffect, useRef, useState } from 'react'
import { Portal } from '@chakra-ui/core'
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd'
import { Star, GripVertical, RotateCcw } from 'lucide-react'
import { Button } from 'components/ui/Button'
import { MediaUploadPreview } from 'components/ui/MediaUploadPreview'
import useLocaleText from 'components/useLocaleText'
import FsLightbox from 'fslightbox-react'
import { useSelector } from 'react-redux'
import { isRtlSelector } from 'redux/selectors/general'
import { mediaGalleryText } from 'components/ui/MediaGallery/literals'
import styles from './DraggableMediaGallery.module.css'
import { MediaReorderGrid } from './MediaReorderGrid'

interface DraggableMediaGalleryProps {
  attachedImages: string[]
  attachedVideos?: string[]
  /** Local edit drafts are distinct from active uploads. */
  draftImageUrls?: Set<string>
  uploadingUrls?: Set<string>
  failedUrls?: Set<string>
  isShowRemoveIcon?: boolean
  onRemoveMedia?: (index: number) => void
  onRetryMedia?: (index: number) => void
  onReorder?: (startIndex: number, endIndex: number) => void
  onImageClick?: (imageIndex: number) => void
  onVideoClick?: (combinedIndex: number) => void
  layout?: 'row' | 'grid'
  staticItems?: React.ComponentProps<typeof MediaReorderGrid>['staticItems']
  noWrap?: boolean
  isDragDisabled?: boolean
}

const DraggableMediaGallery: React.FC<DraggableMediaGalleryProps> = memo(
  ({
    attachedImages,
    attachedVideos = [],
    draftImageUrls,
    uploadingUrls,
    failedUrls,
    onRemoveMedia,
    onRetryMedia,
    onReorder,
    isShowRemoveIcon = true,
    onImageClick,
    onVideoClick,
    layout = 'row',
    staticItems = [],
    noWrap = false,
    isDragDisabled = false,
  }) => {
    const isRtl = useSelector(isRtlSelector)
    const t = useLocaleText(mediaGalleryText)

    const [isOpenLightBox, setIsOpenLightBox] = useState(false)
    const [lightboxIndex, setLightboxIndex] = useState(0)

    const galleryRef = useRef<HTMLDivElement | null>(null)
    const scrollPosition = useRef<{ node: HTMLElement; max: number }>()

    const media = [...attachedImages, ...attachedVideos]
    const mediaTypes = [...attachedImages.map(() => 'image' as const), ...attachedVideos.map(() => 'video' as const)]
    const canReorder = media.length > 1 && !!onReorder && !isDragDisabled
    const physicalRtlOrder = layout === 'row' && isRtl && (canReorder || noWrap)
    const entries = media.map((url, index) => ({ url, index }))
    if (physicalRtlOrder) entries.reverse()

    const openLightbox = (index: number) => {
      setLightboxIndex(index)
      setIsOpenLightBox((prev) => !prev)
    }

    const handleDragEnd = ({ source, destination }: DropResult) => {
      if (!canReorder || !destination || source.index === destination.index) return
      const logicalIndex = (index: number) => (physicalRtlOrder ? media.length - 1 - index : index)
      onReorder?.(logicalIndex(source.index), logicalIndex(destination.index))
    }

    const openMedia = (index: number) => {
      const handler = index < attachedImages.length ? onImageClick : onVideoClick
      if (handler) handler(index)
      else openLightbox(index)
    }

    const renderMedia = (index: number, handleProps: React.HTMLAttributes<HTMLDivElement> | null | undefined) => {
      const url = media[index]
      const uploading = !!uploadingUrls?.has(url)
      const failed = !!failedUrls?.has(url)
      const name = t.item.replace('{index}', String(index + 1))
      const mainPhoto =
        index === 0 && mediaTypes[index] === 'image' && !draftImageUrls?.has(url) && !uploading && !failed

      return (
        <MediaUploadPreview
          src={url}
          mediaType={mediaTypes[index]}
          alt={name}
          previewLabel={t.preview.replace('{name}', name)}
          uploadingLabel={t.uploading.replace('{name}', name)}
          uploading={uploading}
          error={failed}
          errorLabel={t.failed}
          onPreview={() => openMedia(index)}
          previewDisabled={uploading}
          style={{ width: '100%', height: '100%' }}
          removeAction={
            isShowRemoveIcon && onRemoveMedia
              ? {
                  label: t.remove.replace('{name}', name),
                  onClick: () => onRemoveMedia(index),
                }
              : undefined
          }
          overlay={
            <>
              {mainPhoto && (
                <span
                  className={styles.mainPhoto}
                  dir={isRtl ? 'rtl' : 'ltr'}
                  title={t.mainPhoto}
                  aria-label={t.mainPhoto}
                >
                  <Star size={14} color="white" fill="white" />
                </span>
              )}
              {failed && onRetryMedia && (
                <Button
                  type="button"
                  variant="icon"
                  className={styles.retry}
                  aria-label={t.retry.replace('{name}', name)}
                  onClick={() => onRetryMedia(index)}
                >
                  <RotateCcw size={14} />
                </Button>
              )}
              {canReorder && (
                <div
                  {...handleProps}
                  aria-label={t.reorder.replace('{name}', name)}
                  className={styles.dragHandle}
                  dir={isRtl ? 'rtl' : 'ltr'}
                >
                  <GripVertical size={14} />
                </div>
              )}
            </>
          }
        />
      )
    }

    useLayoutEffect(() => {
      if (!physicalRtlOrder) {
        scrollPosition.current = undefined
        return
      }
      const node = galleryRef.current?.parentElement
      if (!node) return
      // RBD requires a physical LTR scroll axis. Preserve the user's distance from its right edge.
      const max = Math.max(0, node.scrollWidth - node.clientWidth)
      const previous = scrollPosition.current
      node.scrollLeft = previous?.node === node ? Math.max(0, node.scrollLeft + max - previous.max) : max
      scrollPosition.current = { node, max }
    }, [physicalRtlOrder, media.length, canReorder, noWrap])

    if (!media.length && !(layout === 'grid' && staticItems.length)) return null

    return (
      <>
        {layout === 'grid' ? (
          <MediaReorderGrid
            staticItems={staticItems}
            items={media}
            disabled={!canReorder}
            rtl={isRtl}
            onReorder={onReorder}
          >
            {(index, handleProps) => <div className={styles.item}>{renderMedia(index, handleProps)}</div>}
          </MediaReorderGrid>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <div
              style={{
                overflowX: canReorder || noWrap ? 'auto' : undefined,
                direction: 'ltr',
                scrollbarWidth: noWrap ? 'none' : undefined,
              }}
            >
              <Droppable droppableId="media-gallery" direction="horizontal" type="media">
                {(provided) => (
                  <div
                    ref={(node) => {
                      galleryRef.current = node
                      provided.innerRef(node)
                    }}
                    {...provided.droppableProps}
                    className={styles.gallery}
                    style={{
                      flexWrap: noWrap || canReorder ? 'nowrap' : 'wrap',
                      minWidth: noWrap || canReorder ? 'max-content' : undefined,
                      flexDirection: isRtl && !physicalRtlOrder ? 'row-reverse' : 'row',
                      justifyContent: physicalRtlOrder ? 'flex-end' : undefined,
                    }}
                  >
                    {entries.map(({ url, index }, physicalIndex) => {
                      return (
                        <Draggable key={url} draggableId={url} index={physicalIndex} isDragDisabled={!canReorder}>
                          {(item, snapshot) => (
                            <div
                              ref={item.innerRef}
                              {...item.draggableProps}
                              className={styles.item}
                              dir={isRtl ? 'rtl' : 'ltr'}
                              style={{
                                ...item.draggableProps.style,
                                boxShadow: snapshot.isDragging ? '0 5px 15px rgba(0,0,0,0.3)' : undefined,
                              }}
                            >
                              {renderMedia(index, item.dragHandleProps)}
                            </div>
                          )}
                        </Draggable>
                      )
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          </DragDropContext>
        )}
        {media.length > 0 && (
          <Portal container={document.getElementById('root')}>
            <FsLightbox
              key={media.join(',')}
              toggler={isOpenLightBox}
              sources={media}
              sourceIndex={lightboxIndex}
              types={mediaTypes}
            />
          </Portal>
        )}
      </>
    )
  }
)

export default DraggableMediaGallery
